import React, {useEffect, useMemo, useState, useCallback} from "react";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Typography from "@material-ui/core/Typography";
import Checkbox from "@material-ui/core/Checkbox";
import ListItem from "@material-ui/core/ListItem";
import ListItemIcon from "@material-ui/core/ListItemIcon";
import ListItemText from "@material-ui/core/ListItemText";
import List from "@material-ui/core/List";
import Chip from "@material-ui/core/Chip";
import WarningIcon from "@material-ui/icons/Warning";
import {Issue, SelectedIssue} from "./portfolio_report";

interface PortfolioReportIssuesDialogProps {
  open: boolean;
  issues: Issue[];
  selectedIssues: SelectedIssue[];
  onClose: () => void;
  onSave: (selectedIssues: SelectedIssue[]) => void;
  formatIssueName: (name: string) => string;
}

const styles = {
  dialogPaper: {
    width: "750px",
    height: "800px",
    maxHeight: "85vh",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  },
  dialogTitle: {
    padding: "1em",
  },
  dialogContent: {
    padding: "1em",
    overflowY: "hidden",
    display: "flex",
    flexDirection: "column",
    flex: 1,
  },
  searchField: {
    marginBottom: "1em",
    width: "100%",
  },
  selectedSection: {
    marginBottom: "1em",
  },
  chipContainer: {
    display: "flex",
    flexWrap: "wrap",
    gap: "8px",
    marginBottom: "1em",
    maxHeight: "120px",
    overflowY: "auto",
    padding: "4px",
    border: "1px solid rgba(0, 0, 0, 0.08)",
    borderRadius: "4px",
    backgroundColor: "rgba(0, 0, 0, 0.02)",
  },
  chip: {
    margin: "2px",
  },
  chipLabel: {
    display: "flex",
    alignItems: "center",
  },
  issuesListContainer: {
    flex: 1,
    overflowY: "auto",
    border: "1px solid rgba(0, 0, 0, 0.08)",
    borderRadius: "4px",
  },
  dialogActions: {
    padding: "1em",
  },
  warningIcon: {
    color: "#f57c00",
    fontSize: "1rem",
    marginLeft: "4px",
    verticalAlign: "middle",
  },
} as const;

const PortfolioReportIssuesDialog: React.FC<PortfolioReportIssuesDialogProps> = ({
  open,
  issues,
  selectedIssues: initialSelectedIssues,
  onClose,
  onSave,
  formatIssueName,
}) => {
  const [dialogSelectedIssues, setDialogSelectedIssues] = useState<
    SelectedIssue[]
  >([]);
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    if (open) {
      setDialogSelectedIssues([...initialSelectedIssues]);
    }
  }, [open, initialSelectedIssues]);

  useEffect(() => {
    if (!open) {
      setSearchQuery("");
    }
  }, [open]);

  const filteredIssues = useMemo(() => {
    return issues.filter(issue =>
      issue.name.toLowerCase().includes(searchQuery.toLowerCase()),
    );
  }, [issues, searchQuery]);

  const handleToggleIssue = useCallback(
    (issue: Issue) => {
      const formattedName = formatIssueName(issue.display_name || issue.name);

      setDialogSelectedIssues(prev => {
        const existingIndex = prev.findIndex(item => {
          if (issue.master_id && item.master_id) {
            return item.master_id === issue.master_id;
          }
          return item.name === formattedName;
        });

        if (existingIndex >= 0) {
          return prev.filter((_, i) => i !== existingIndex);
        } else {
          return [
            ...prev,
            {
              master_id: issue.master_id,
              name: formattedName,
            },
          ];
        }
      });
    },
    [formatIssueName],
  );

  const handleRemoveDialogIssue = useCallback(
    (issueToRemove: SelectedIssue) => {
      setDialogSelectedIssues(prev =>
        prev.filter(item => {
          if (issueToRemove.master_id && item.master_id) {
            return item.master_id !== issueToRemove.master_id;
          }
          return item.name !== issueToRemove.name;
        }),
      );
    },
    [],
  );

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
  };

  const handleSave = () => {
    onSave(dialogSelectedIssues);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      PaperProps={{
        style: styles.dialogPaper,
      }}
    >
      <DialogTitle id="issue-dialog-title" style={styles.dialogTitle}>
        Select Issues
      </DialogTitle>

      <DialogContent style={styles.dialogContent}>
        <TextField
          label="Search issues"
          variant="outlined"
          size="small"
          style={styles.searchField}
          value={searchQuery}
          onChange={handleSearchChange}
        />

        {dialogSelectedIssues.length > 0 && (
          <div style={styles.selectedSection}>
            <Typography variant="subtitle2" gutterBottom>
              Selected ({dialogSelectedIssues.length}):
            </Typography>
            <div style={styles.chipContainer}>
              {dialogSelectedIssues.map((issue, index) => (
                <Chip
                  key={issue.master_id || `name-${index}`}
                  label={
                    <span style={styles.chipLabel}>
                      {issue.name}
                      {!issue.master_id && (
                        <WarningIcon style={styles.warningIcon} />
                      )}
                    </span>
                  }
                  onDelete={() => handleRemoveDialogIssue(issue)}
                  style={styles.chip}
                  size="small"
                />
              ))}
            </div>
          </div>
        )}

        <div style={styles.issuesListContainer}>
          <List disablePadding>
            {filteredIssues.map(issue => {
              const formattedName = formatIssueName(
                issue.display_name || issue.name,
              );

              const isSelected = dialogSelectedIssues.some(item => {
                if (issue.master_id && item.master_id) {
                  return item.master_id === issue.master_id;
                }
                return item.name === formattedName;
              });

              return (
                <ListItem
                  key={issue.id}
                  button
                  onClick={() => handleToggleIssue(issue)}
                  dense
                  divider
                >
                  <ListItemIcon>
                    <Checkbox
                      edge="start"
                      checked={isSelected}
                      tabIndex={-1}
                      disableRipple
                    />
                  </ListItemIcon>
                  <ListItemText
                    primary={formattedName}
                    primaryTypographyProps={{
                      noWrap: false,
                      style: {wordBreak: "break-word"},
                    }}
                  />
                </ListItem>
              );
            })}

            {filteredIssues.length === 0 && (
              <ListItem>
                <ListItemText
                  primary="No issues found"
                  secondary="Try a different search term"
                />
              </ListItem>
            )}
          </List>
        </div>
      </DialogContent>

      <DialogActions style={styles.dialogActions}>
        <Button onClick={onClose} color="default">
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          color="primary"
          variant="contained"
          disabled={dialogSelectedIssues.length === 0}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PortfolioReportIssuesDialog;
